import { useEffect, FunctionComponent, useState } from 'react';
import './Settlement.scss';
import { useParams, useHistory } from 'react-router-dom';
import DashboardWrap from '../../components/DashboardWrap';
import useCustomQuery from '../../network/useCustomQuery';
import DashboardHeader from '../../components/DashboardHeader';
import {

  GET_SETTLEMENT_BY_ID,
  GET_SETTLEMENT_TRANSACTIONS,
} from '../../graphql/queries';
import Table from '../../components/Table';
import { cleanUpSettlementTransactions } from '../../utils/helpers';
import SettlementFlashCardNew from '../../components/SettlementFlashCardNew';
import useGetQueryParams from '../../customHooks/useGetQueryParams';
import SettledAmountIcon from '../../svgs/SettledAmountIcon';
import TotalSalesIcon from '../../svgs/TotalSalesIcon';
import TotalChargesIcon from '../../svgs/TotalChargesIcon';
import EmptySettlementIcon from '../../svgs/EmptySettlementIcon';
import EmptyRecord from '../../components/EmptyRecord';
import useToast from '../../customHooks/useToast';
import generatePDF from '../../utils/generatePdf';
import generateCSV from '../../utils/generateCSV';
import useFilterMethods from '../../customHooks/useFilterMethods';

export interface ISettlementTransactions {
  settlementData?: any;
}
const limit = 15;
const SettlementTransactions: FunctionComponent<ISettlementTransactions> = () => {
  const [
    getSettlementTransactions,
    getSettlementTransactionsLoading,
    rawData,
  ] = useCustomQuery({
    query: GET_SETTLEMENT_TRANSACTIONS,
    onError: () => {},
    onData: () => {},
  });
  const [getSettlement, , settlement] = useCustomQuery({
    query: GET_SETTLEMENT_BY_ID,
    onError: () => {},
    onData: () => {},
  });
  const { settlementId } = useParams<{ settlementId: string }>();
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const page = Number(getQuery('page'));
  const search = getQuery('search') || '';
  const { push } = useHistory();

  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };

  if (!page) {
    push(`/settlement/transactions/${settlementId}?page=1&search=${search}`);
  }
  useEffect(() => {
    if (settlementId) {
      getSettlement({ variables: { id: settlementId } });
    }
  }, [settlementId, page]);

  useEffect(() => {
    if (settlementId) {
      const result = getFilterQueries();
      getSettlementTransactions({
        variables: {
          id: settlementId,
          offset: (page - 1) * limit,
          limit,
          search,
          filters: {
            from: result.from,
            to: result.to,
            status: result.status,
            channel: result.channel,
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
    }
  }, [settlementId, page, search]);

  const [settlementTransactions, setSettlementTransactions] = useState(null);

  useEffect(() => {
    if (rawData?.getTransactionsBySettlementId?.transactions) {
      setSettlementTransactions(rawData);
    }
  }, [rawData]);

  const onSearchChange = (e) => {
    push(`/settlement/transactions/${settlementId}?page=1&search=${encodeURIComponent(e.target.value)}`);
  };

  const settlementRec = settlement?.getSettlementById;
  const details = [
    {
      name: 'Settled Amount',
      value: ((settlementRec?.totalAmount || 0) - (settlementRec?.vendorFees || 0)) || 0,
      Icon: SettledAmountIcon,
    },
    {
      name: 'Number of Transactions',
      value: settlementTransactions?.getTransactionsBySettlementId?.count,
      Icon: SettledAmountIcon,
      notAmount: true,
    },
    {
      name: 'Sales',
      value: settlementRec?.totalAmount,
      Icon: TotalSalesIcon,
    },
    {
      name: 'Charges',
      value: settlementRec?.vendorFees,
      Icon: TotalChargesIcon,
    },
  ];

  const Toast = useToast();

  const [getExportRecord] = useCustomQuery({
    query: GET_SETTLEMENT_TRANSACTIONS,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          search,
          id: settlementId,
          filters: {
            from: result.from,
            to: result.to,
            status: result.status,
            channel: result.channel,
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
      if (output?.data?.getTransactionsBySettlementId?.transactions) {
        // eslint-disable-next-line max-len
        const rec = cleanUpSettlementTransactions(output?.data?.getTransactionsBySettlementId?.transactions);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        func({
          payload: rec,
          title: 'SETTLEMENT-TRANSACTIONS',
          selectedHeaders: [
            'S/N',
            'dateTime',
            'txRef',
            'amount',
            'fees',
            'status',
          ],
          formatHeaders: {
            dateTime: 'Created On',
            txRef: 'Reference',
            amount: 'Sales',
            fees: 'Charges',
          },
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  const onFilterSubmit = (result) => {
    getSettlementTransactions({
      variables: {
        offset: 0,
        limit,
        id: settlementId,
        search,
        filters: {
          from: result.from,
          to: result.to,
          status: result.status,
          channel: result.channel,
          minAmount: result.min ? Number(result.min) : 0,
          maxAmount: result.max ? Number(result.max) : 0,
        },
      },
    });
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: ['dateRange', 'status', 'amountRange'],
    defaultUrl: `/settlement/transactions/${settlementId}?page=1&search=${search}`,
    cb: onFilterSubmit,
    Toast,
  });

  return (
    <DashboardWrap>
      <div className="settlement-merchant">
        <DashboardHeader
          title="Single Settlement"
          smallSubTitle=""
          filterObj={filterObj}
          exportObj={{
            handleExport,
          }}
        />

        <div className="settlement-card-covers">
          {details?.map?.(({
            name, value, Icon, notAmount,
          }, id) => (
            <SettlementFlashCardNew
              name={name}
              key={`${name}-${id}`}
              value={value}
              Icon={Icon}
              notAmount={notAmount}
            />
          ))}
        </div>

        <div className="settlement-table-cover">
          <DashboardHeader
            smallSubTitle={`(${settlementTransactions?.getTransactionsBySettlementId?.count})`}
            searchPlaceholder="Look for transaction"
            search={search}
            onSearchChange={onSearchChange}
            searchLoading={getSettlementTransactionsLoading}
          />
          {settlementTransactions?.getTransactionsBySettlementId?.count ? (
            <Table
              refreshing={getSettlementTransactionsLoading}
              count={settlementTransactions?.getTransactionsBySettlementId?.count}
              contents={cleanUpSettlementTransactions(
                settlementTransactions?.getTransactionsBySettlementId?.transactions,
              )}
              page={page}
              setPage={setPage}
              reference="id"
              hideDominantImage
              allowKeyArr={[
                ['date', 'time'],
                'txRef',
                'amount',
                'fees',
                'status',
              ]}
              mobileAllowKeyArr={[
                'status',
                ['amount', 'fees'],
                [
                  'txRef', 'date', 'time',
                ],
              ]}
              minHeight="calc(100vh - 400px)"
              formatLabels={{
                date: 'Created On',
                txRef: 'Reference',
                amount: 'Sales',
                fees: 'Charges',
              }}
            />
          ) : (
            <EmptyRecord
              height="calc(100vh - 400px)"
              title="No Settlement record yet"
              main="Settlements occur after you recieve payments from customers"
              Icon={EmptySettlementIcon}
              loading={getSettlementTransactionsLoading}
            />
          )}
        </div>
      </div>
    </DashboardWrap>
  );
};

export default SettlementTransactions;
